import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tasksApi, projectsApi } from '../api/client';
import { useUIStore, useCalendarStore, useDataStore } from '../store';
import type { Task, Project } from '../types';
import {
  CheckCircle2,
  Circle,
  Clock,
  Calendar,
  Grid3X3,
  List,
  ChevronLeft,
  ChevronRight,
  Plus,
} from 'lucide-react';

// 获取一周的开始日期（周日）
function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.setDate(diff));
}

// 获取一周的日期数组
function getWeekDays(weekStart: Date): Date[] {
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart);
    day.setDate(weekStart.getDate() + i);
    days.push(day);
  }
  return days;
}

// 格式化日期
function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

// 格式化时间
function formatTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
}

// 获取日期在一周中的索引
function getDayIndex(date: Date): number {
  return date.getDay();
}

export function WeekView() {
  const { weekViewMode, setWeekViewMode, openTaskModal, setSelectedTaskId } = useUIStore();
  const { currentDate, goToToday, goToPrevious, goToNext } = useCalendarStore();
  const { setTasks, tasks } = useDataStore();

  // 获取任务列表
  const { data: tasksData, isLoading } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => tasksApi.getAll(),
  });

  // 获取项目列表
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.getAll(),
  });

  useEffect(() => {
    if (tasksData?.success && tasksData.data) {
      setTasks(tasksData.data);
    }
  }, [tasksData, setTasks]);

  const projects = projectsData?.success ? projectsData.data : [];

  // 计算当前周的日期
  const weekStart = getWeekStart(currentDate);
  const weekDays = getWeekDays(weekStart);

  // 星期名称
  const dayNames = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

  // 过滤当前周的任务
  const weekTasks = useMemo(() => {
    const start = formatDate(weekDays[0]);
    const end = formatDate(weekDays[6]);
    
    return tasks.filter((task) => {
      if (!task.startTime && !task.dueDate) return false;
      const taskDate = task.startTime || task.dueDate;
      if (!taskDate) return false;
      const date = taskDate.split('T')[0];
      return date >= start && date <= end;
    });
  }, [tasks, weekDays]);

  // 按日期分组的任务
  const tasksByDay = useMemo(() => {
    const grouped: Record<number, Task[]> = {};
    for (let i = 0; i < 7; i++) {
      grouped[i] = [];
    }
    
    weekTasks.forEach((task) => {
      const taskDate = new Date(task.startTime || task.dueDate || '');
      const dayIndex = getDayIndex(taskDate);
      if (!grouped[dayIndex]) grouped[dayIndex] = [];
      grouped[dayIndex].push(task);
    });

    // 每天按时间排序
    for (let i = 0; i < 7; i++) {
      grouped[i].sort((a, b) => {
        const timeA = a.startTime || '';
        const timeB = b.startTime || '';
        return timeA.localeCompare(timeB);
      });
    }

    return grouped;
  }, [weekTasks]);

  // 按项目分组的任务
  const tasksByProject = useMemo(() => {
    const grouped: Record<string, Task[]> = {};
    
    weekTasks.forEach((task) => {
      if (!grouped[task.projectId]) {
        grouped[task.projectId] = [];
      }
      grouped[task.projectId].push(task);
    });

    // 每个项目内按日期排序
    Object.keys(grouped).forEach((projectId) => {
      grouped[projectId].sort((a, b) => {
        const dateA = (a.startTime || a.dueDate || '').split('T')[0];
        const dateB = (b.startTime || b.dueDate || '').split('T')[0];
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        const timeA = a.startTime || '';
        const timeB = b.startTime || '';
        return timeA.localeCompare(timeB);
      });
    });

    return grouped;
  }, [weekTasks]);

  // 获取状态图标
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-blue-600" />;
      default:
        return <Circle className="w-4 h-4 text-gray-400" />;
    }
  };

  // 获取优先级颜色
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'border-l-red-500';
      case 'medium':
        return 'border-l-amber-500';
      case 'low':
        return 'border-l-green-500';
      default:
        return 'border-l-gray-300';
    }
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTaskId(task.id);
    openTaskModal(task.id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 工具栏 */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          {/* 视图模式切换 */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setWeekViewMode('timeline')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                weekViewMode === 'timeline'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
              时间线
            </button>
            <button
              onClick={() => setWeekViewMode('project-grouped')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                weekViewMode === 'project-grouped'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3X3 className="w-4 h-4" />
              项目分组
            </button>
          </div>
        </div>

        {/* 日期导航 */}
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrevious}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goToToday}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-all border border-gray-300"
          >
            今天
          </button>
          <button
            onClick={goToNext}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* 时间线模式 */}
      {weekViewMode === 'timeline' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* 表头 */}
          <div className="grid grid-cols-7 border-b border-gray-200">
            {weekDays.map((day, index) => {
              const isToday = formatDate(day) === formatDate(new Date());
              return (
                <div
                  key={index}
                  className={`p-3 text-center border-r border-gray-200 last:border-r-0 ${
                    isToday ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className={`text-sm ${isToday ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                    {dayNames[index]}
                  </div>
                  <div className={`text-lg font-semibold ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                    {day.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 任务网格 */}
          <div className="grid grid-cols-7 min-h-[400px]">
            {weekDays.map((day, dayIndex) => {
              const dayTasks = tasksByDay[dayIndex] || [];
              const isToday = formatDate(day) === formatDate(new Date());
              
              return (
                <div
                  key={dayIndex}
                  className={`p-2 border-r border-gray-200 last:border-r-0 min-h-[400px] ${
                    isToday ? 'bg-blue-50/30' : ''
                  }`}
                >
                  <div className="space-y-2">
                    {dayTasks.map((task) => (
                      <div
                        key={task.id}
                        onClick={() => handleTaskClick(task)}
                        className={`p-2 bg-white rounded-lg border border-l-4 shadow-sm cursor-pointer hover:shadow-md transition-all ${getPriorityColor(
                          task.priority
                        )}`}
                      >
                        <div className="flex items-start gap-2">
                          {getStatusIcon(task.status)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {task.title}
                            </p>
                            {task.startTime && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                {formatTime(task.startTime)} - {formatTime(task.endTime)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* 添加任务按钮 */}
                    <button
                      onClick={() => openTaskModal()}
                      className="w-full p-2 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 hover:border-blue-300 hover:text-blue-500 transition-all flex items-center justify-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="text-xs">添加</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 项目分组模式 */}
      {weekViewMode === 'project-grouped' && (
        <div className="space-y-4">
          {projects.map((project) => {
            const projectTasks = tasksByProject[project.id] || [];
            if (projectTasks.length === 0) return null;

            return (
              <div
                key={project.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                {/* 项目标题 */}
                <div
                  className="px-4 py-3 border-b border-gray-200 flex items-center gap-3"
                  style={{ backgroundColor: `${project.color}15` }}
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: project.color }}
                  />
                  <h3 className="font-semibold text-gray-900">{project.name}</h3>
                  <span className="text-sm text-gray-500">({projectTasks.length} 个任务)</span>
                </div>

                {/* 任务列表 */}
                <div className="p-4">
                  <div className="grid grid-cols-7 gap-2">
                    {weekDays.map((day, dayIndex) => {
                      const dayTasks = projectTasks.filter((task) => {
                        const taskDate = new Date(task.startTime || task.dueDate || '');
                        return getDayIndex(taskDate) === dayIndex;
                      });

                      return (
                        <div key={dayIndex} className="min-h-[150px]">
                          <div className="text-xs text-gray-500 text-center mb-2">
                            {dayNames[dayIndex]} {day.getDate()}
                          </div>
                          <div className="space-y-2">
                            {dayTasks.map((task) => (
                              <div
                                key={task.id}
                                onClick={() => handleTaskClick(task)}
                                className={`p-2 bg-white rounded-lg border border-l-4 shadow-sm cursor-pointer hover:shadow-md transition-all ${getPriorityColor(
                                  task.priority
                                )}`}
                              >
                                <div className="flex items-start gap-1">
                                  {getStatusIcon(task.status)}
                                  <p className="text-xs font-medium text-gray-900 truncate flex-1">
                                    {task.title}
                                  </p>
                                </div>
                                {task.startTime && (
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {formatTime(task.startTime)}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}

          {Object.keys(tasksByProject).length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-lg font-medium text-gray-900">本周暂无任务</p>
              <p className="text-sm text-gray-500 mt-1">点击"新建任务"开始规划</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
